import React, { useState, useEffect } from "react";
import axios from "axios";
import { BookOpen, ExternalLink } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicationsPage() {
  const [pubs, setPubs] = useState([]);
  const [type, setType] = useState("All");
  const [language, setLanguage] = useState("All");

  useEffect(() => {
    axios.get(`${API}/publications`).then((r) => setPubs(r.data)).catch(() => {});
  }, []);

  const types = ["All", ...new Set(pubs.map((p) => p.type))];
  const languages = ["All", ...new Set(pubs.map((p) => p.language))];
  const shown = pubs.filter((p) => (type === "All" || p.type === type) && (language === "All" || p.language === language));

  return (
    <div>
      <PageHero topic="sports magazines newspapers press" title="Publications & Media Directory" subtitle="Sports magazines, newspapers, science journals and podcasts from around the world — by country, language and access model." listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <AIContent topic="The world of sports publishing — from daily papers to science journals and podcasts" context="Editorial on the sports media landscape: legacy dailies like L'Équipe and Marca, longform magazines, peer-reviewed sports science journals, and the podcast boom. Why plural, multilingual sports media matters." />

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap gap-2" data-testid="publication-type-filters">
            {types.map((t) => (
              <button key={t} data-testid={`pub-type-${t.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setType(t)} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${type === t ? "bg-[#0055FF] text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-[#0055FF] hover:text-[#0055FF]"}`}>
                {t === "All" ? <T>All</T> : <T>{t}</T>}
              </button>
            ))}
          </div>
          <select data-testid="pub-language-select" value={language} onChange={(e) => setLanguage(e.target.value)} className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-bold text-slate-600 outline-none focus:border-[#0055FF]">
            {languages.map((l) => <option key={l}>{l}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="publications-grid">
          {shown.map((p) => (
            <div key={p.id} data-testid={`publication-card-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0055FF] text-white"><BookOpen className="h-5 w-5" /></span>
                <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold uppercase text-[#0055FF]"><T>{p.type}</T></span>
              </div>
              <h3 className="mt-3 text-lg font-bold text-slate-900">{p.title}</h3>
              <p className="text-sm font-semibold text-slate-400">{p.country} · {p.language}</p>
              <p className="mt-2 text-sm text-slate-600"><T>{p.scope}</T></p>
              <div className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 text-sm text-slate-600">
                <p><span className="font-bold"><T>Publisher</T>:</span> {p.publisher}</p>
                <p><span className="font-bold"><T>Frequency</T>:</span> <T>{p.frequency}</T> · <span className="font-bold"><T>Access</T>:</span> <T>{p.access}</T></p>
              </div>
              <a href={p.url} target="_blank" rel="noreferrer" data-testid={`publication-link-${p.id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#0055FF] hover:underline">
                <T>Visit</T> <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          ))}
        </div>
        <p className="text-sm italic text-slate-400"><T>Listing is not endorsement. Details reflect publicly available information at time of curation.</T></p>
      </div>
    </div>
  );
}
